/**
 * @description Super Admin create Sales Org under a Channel Partner (LYN-323)
 *
 * Key features:
 * - New form on Channel Partners → that CP → Sales Orgs → Create
 * - Existing under-CP POST (`channelPartnerId` from the page URL)
 * - After save, return to that CP’s Sales Orgs tab
 *
 * @dependencies
 * - adminApi: getChannelPartner(), getTemplates(), createTenant()
 * - createSalesOrgUnderCp: URL parent id, payload, return path
 * - getSoOccupancyDoorUrl: same slug preview as today’s create-SO
 *
 * @notes
 * - Do not reuse the LynxPay wizard or its create route
 * - Overlay is not read for parent id
 * - Fields = today’s create-SO: name, slug, Admin User + sendInvite + required template
 * - No CP picker, branding, domain, or other CP-only fields
 * - Occupancy (including `towtech`) is enforced server-side
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { createTenant, getChannelPartner, getTemplates } from '../../services/api/adminApi';
import { getSoOccupancyDoorUrl } from '../../config/domains';
import {
  buildCreateSalesOrgUnderCpBody,
  channelPartnerDetailPath,
  resolveCreateParentChannelPartnerId,
  SALES_ORGS_TAB_QUERY
} from '../../utils/createSalesOrgUnderCp';
import { US_IANA_TIMEZONES } from '../../constants/usIanaTimezones';

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

function autoSlug(value) {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 20);
}

function ChannelPartnerSalesOrgCreate() {
  const { channelPartnerId: urlChannelPartnerId } = useParams();
  const channelPartnerId = resolveCreateParentChannelPartnerId(urlChannelPartnerId);
  const navigate = useNavigate();
  const salesOrgsHref = channelPartnerDetailPath(channelPartnerId, SALES_ORGS_TAB_QUERY);

  const [partner, setPartner] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [inviteNotice, setInviteNotice] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [form, setForm] = useState({
    organizationName: '',
    slug: '',
    adminEmail: '',
    adminFirstName: '',
    adminLastName: '',
    timezone: '',
    sendInvite: false
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!channelPartnerId) {
        setLoadError('Channel partner is missing from the URL.');
        setLoading(false);
        return;
      }

      try {
        const [partnerResult, templatesResult] = await Promise.all([
          getChannelPartner(channelPartnerId),
          getTemplates()
        ]);
        if (cancelled) return;
        if (partnerResult.success) {
          setPartner(partnerResult.data);
        }
        if (templatesResult.success) {
          const rows = templatesResult.data || [];
          setTemplates(rows);
          const firstActive = rows.find((row) => row.isActive) || rows[0];
          if (firstActive?.id) {
            setSelectedTemplateId(firstActive.id);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err.response?.data?.error?.message || 'Failed to load channel partner');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [channelPartnerId]);

  const updateForm = useCallback((field, value) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'organizationName' && prev.slug === autoSlug(prev.organizationName)) {
        updated.slug = autoSlug(value);
      }
      return updated;
    });
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const validate = () => {
    const errors = {};
    if (!form.organizationName.trim() || form.organizationName.trim().length < 2) {
      errors.organizationName = 'Organization name is required (min 2 characters)';
    }
    if (!form.slug.trim()) {
      errors.slug = 'Slug is required';
    } else if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(form.slug)) {
      errors.slug = 'Slug must be lowercase letters, numbers, and single hyphens';
    } else if (form.slug.length < 3 || form.slug.length > 20) {
      errors.slug = 'Slug must be 3-20 characters';
    }
    if (!form.adminEmail.trim()) {
      errors.adminEmail = 'Admin email is required';
    } else if (!isValidEmail(form.adminEmail.trim())) {
      errors.adminEmail = 'Please enter a valid email address';
    }
    if (!selectedTemplateId) {
      errors.templateId = 'Template is required';
    }
    if (!form.timezone) {
      errors.timezone = 'Timezone is required';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setSubmitError(null);
    setInviteNotice(null);
    const idempotencyKey = crypto.randomUUID();

    try {
      const result = await createTenant(
        buildCreateSalesOrgUnderCpBody({
          organizationName: form.organizationName.trim(),
          slug: form.slug.toLowerCase().trim(),
          templateId: selectedTemplateId,
          adminEmail: form.adminEmail.trim().toLowerCase(),
          adminFirstName: form.adminFirstName.trim() || undefined,
          adminLastName: form.adminLastName.trim() || undefined,
          channelPartnerId,
          timezone: form.timezone,
          sendInvite: form.sendInvite === true
        }),
        idempotencyKey
      );

      if (result.success) {
        if (form.sendInvite === true && result.data?.inviteSent !== true) {
          setInviteNotice(
            result.message
            || 'Sales organization created but invitation could not be sent. You can resend later.'
          );
          return;
        }
        navigate(salesOrgsHref, { replace: true });
      }
    } catch (err) {
      const errorCode = err.response?.data?.error?.code;
      const errorMessage = err.response?.data?.error?.message;
      if (errorCode === 'INVALID_SLUG' || errorCode === 'SLUG_TOO_LONG') {
        setSubmitError(errorMessage || 'That slug is taken or reserved.');
      } else if (errorCode === 'SO_ROLE_MISSING' || errorCode === 'SO_ROLE_ASSIGN_FAILED') {
        setSubmitError(errorMessage || 'Wristband catalog role app:{env}:so is missing. Create fails closed.');
      } else if (errorCode === 'INVALID_CHANNEL_PARTNER') {
        setSubmitError(errorMessage || 'Channel partner not found or inactive.');
      } else {
        setSubmitError(errorMessage || 'Failed to create sales organization.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-800 border-t-transparent" />
      </div>
    );
  }

  if (loadError || !partner) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] text-center">
        <AlertCircle className="w-10 h-10 text-red-400 mb-3" />
        <p className="text-gray-900 font-medium mb-1">Failed to load channel partner</p>
        <p className="text-gray-500 text-sm">{loadError || 'Channel partner not found'}</p>
        <button
          type="button"
          onClick={() => navigate('/channel-partners')}
          className="mt-4 text-sm text-indigo-600 hover:text-indigo-500"
        >
          Back to Channel Partners
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-xl">
      <button
        type="button"
        onClick={() => navigate(salesOrgsHref)}
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Sales Orgs
      </button>
      <h1 className="text-xl font-semibold text-gray-900">Create Sales Org</h1>
      <p className="text-sm text-gray-500 mt-0.5 mb-6">
        New sales organization under {partner.name}. Same occupancy pool as Channel Partner slugs.
      </p>

      {submitError && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p>{submitError}</p>
        </div>
      )}
      {inviteNotice && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p>{inviteNotice}</p>
            <button
              type="button"
              onClick={() => navigate(salesOrgsHref, { replace: true })}
              className="mt-2 text-sm font-medium text-amber-900 underline"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 bg-white border border-gray-200 rounded-xl p-6">
        <div className="space-y-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Template</p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Template <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedTemplateId}
              onChange={(event) => {
                setSelectedTemplateId(event.target.value);
                setFieldErrors((prev) => {
                  if (!prev.templateId) return prev;
                  const next = { ...prev };
                  delete next.templateId;
                  return next;
                });
              }}
              className={`w-full px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 ${
                fieldErrors.templateId ? 'border-red-300 bg-red-50' : 'border-gray-200'
              }`}
            >
              {templates.length === 0 && <option value="">No templates available</option>}
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
            {fieldErrors.templateId && (
              <p className="text-xs text-red-600 mt-1">{fieldErrors.templateId}</p>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Sales organization</p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organization name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.organizationName}
              onChange={(e) => updateForm('organizationName', e.target.value)}
              placeholder="Northwind Sales"
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 ${
                fieldErrors.organizationName ? 'border-red-300 bg-red-50' : 'border-gray-200'
              }`}
            />
            {fieldErrors.organizationName && (
              <p className="text-xs text-red-600 mt-1">{fieldErrors.organizationName}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Slug <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.slug}
              onChange={(e) => updateForm('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').substring(0, 20))}
              maxLength={20}
              placeholder="northwind-sales"
              className={`w-full px-3 py-2 border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 ${
                fieldErrors.slug ? 'border-red-300 bg-red-50' : 'border-gray-200'
              }`}
            />
            {fieldErrors.slug ? (
              <p className="text-xs text-red-600 mt-1">{fieldErrors.slug}</p>
            ) : (
              <p className="text-xs text-gray-400 mt-1">
                Domain preview:{' '}
                <span className="font-medium text-gray-600">{getSoOccupancyDoorUrl({ slug: form.slug || 'slug' }, partner)}</span>
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timezone <span className="text-red-500">*</span>
            </label>
            <select
              value={form.timezone}
              onChange={(e) => updateForm('timezone', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 ${
                fieldErrors.timezone ? 'border-red-300 bg-red-50' : 'border-gray-200'
              }`}
            >
              <option value="">Select timezone</option>
              {US_IANA_TIMEZONES.map((zone) => (
                <option key={zone.value} value={zone.value}>
                  {zone.label}
                </option>
              ))}
            </select>
            {fieldErrors.timezone && (
              <p className="text-xs text-red-600 mt-1">{fieldErrors.timezone}</p>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Admin User</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First name</label>
              <input
                type="text"
                value={form.adminFirstName}
                onChange={(e) => updateForm('adminFirstName', e.target.value)}
                placeholder="Pat"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last name</label>
              <input
                type="text"
                value={form.adminLastName}
                onChange={(e) => updateForm('adminLastName', e.target.value)}
                placeholder="Lee"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={form.adminEmail}
              onChange={(e) => updateForm('adminEmail', e.target.value)}
              placeholder="admin@northwind.example"
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 ${
                fieldErrors.adminEmail ? 'border-red-300 bg-red-50' : 'border-gray-200'
              }`}
            />
            {fieldErrors.adminEmail && (
              <p className="text-xs text-red-600 mt-1">{fieldErrors.adminEmail}</p>
            )}
          </div>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={form.sendInvite}
              onChange={(e) => updateForm('sendInvite', e.target.checked)}
              className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span>
              <span className="text-sm font-medium text-gray-700">Send invitation</span>
              <span className="block text-xs text-gray-500 mt-0.5">
                Email a branded invite so they can set a password. Leave unchecked to create only.
              </span>
            </span>
          </label>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-indigo-500 disabled:opacity-60"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Create sales org
          </button>
        </div>
      </form>
    </div>
  );
}

export default ChannelPartnerSalesOrgCreate;
